import type { ReactNode } from 'react';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@artstorefronts/ui';
import { DYNAMIC_MESSAGE_STEP_CODES, WELCOME_GIFT_STEP_CODE, suggestionAxisFor } from '@arthelper/shared/bounties/kind';
import { BOOST_DAYS, isBoostableStep, MULTIPLIER_MAX, MULTIPLIER_MIN } from '@arthelper/shared/bounties/multiplier';
import { MAX_TARGET_POSTS, TARGET_POSTS_STEP_CODE } from '@arthelper/shared/bounties/target-posts';
import { bountyTargetError } from './bounty-name';
import {
  ADMIN_BOUNTY_CHASERS,
  KIND_LABEL,
  periodWord,
  type AdminBounty,
  type BountyCatalogEntry,
  type BountyKind,
} from './bounty-shared';
import { useQuery } from '@apollo/client';
import { digitsOnly } from './member-gates';
import type { ProfileCompletenessTarget } from '../quests/quest-shared';
import { ChannelRow, ReminderSelect, type Reminder } from '../delivery-channels';

export type { Reminder };

/**
 * THE STEP BOUNTY FORM (#4118) — every setting of a bounty that pays for a quest step,
 * grouped by the question it answers: what it pays for, when it runs, who it is for, how
 * members hear about it, its words, and the fine print. The editor owns the state.
 */

export type BountyForm = {
  kind: BountyKind;
  name: string;
  slug: string;
  emoji: string;
  stepCode: string;
  target: string;
  pointsReward: string;
  /** '' for none, else '2'–'5'. Only a community-post bounty offers it. */
  multiplier: string;
  cadence: AdminBounty['cadence'];
  inSeries: boolean;
  seriesDay: string;
  enrollment: AdminBounty['enrollment'];
  channelNotify: boolean;
  channelPin: boolean;
  channelEmail: boolean;
  channelDigest: boolean;
  showOnPoints: boolean;
  showOnBounties: boolean;
  notifyReminder: Reminder | null;
  emailReminder: Reminder | null;
  intents: string;
  careerStages: string;
  countryCodes: string;
  /** Days; blank = no minimum. */
  minAccountAgeDays: string;
  requireTier2Activated: boolean;
  /** Lifetime reputation points; blank = no minimum. */
  minReputationPoints: string;
  profileCompleteness: ProfileCompletenessTarget | 'any';
  requireHumanMadeArt: boolean;
  /** Paid bounty periods; blank = no minimum. */
  minBountiesCompleted: string;
  priority: string;
  label: string;
  why: string;
  evidence: string;
  cta: string;
  /** A hot-thread item's reply bar. */
  minReplies: string;
  /** A comment-on-posts bounty's posts: one link, slug or id per line. */
  postRefs: string;
  config: Record<string, string>;
};

type SetField = <K extends keyof BountyForm>(key: K, value: BountyForm[K]) => void;

/** A titled group of fields. The line under the title is one sentence, or none. */
export function FormSection({
  title,
  hint,
  testId,
  children,
}: {
  title: string;
  hint?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-gray-200 pt-5 first:border-t-0 first:pt-0" data-testid={testId}>
      <div>
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {hint ? <p className="mt-0.5 text-xs text-gray-500">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function CadenceSelect({
  value,
  disabled,
  onChange,
}: {
  value: BountyForm['cadence'];
  disabled?: boolean;
  onChange: (value: BountyForm['cadence']) => void;
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={(next) => onChange(next as BountyForm['cadence'])}>
      <SelectTrigger id="b-cadence">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="once">Once</SelectItem>
        <SelectItem value="daily">Every day</SelectItem>
        <SelectItem value="weekly">Every week</SelectItem>
        <SelectItem value="monthly">Every month</SelectItem>
      </SelectContent>
    </Select>
  );
}

function EnrollmentSelect({
  value,
  disabled,
  onChange,
}: {
  value: BountyForm['enrollment'];
  disabled?: boolean;
  onChange: (value: BountyForm['enrollment']) => void;
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={(next) => onChange(next as BountyForm['enrollment'])}>
      <SelectTrigger id="b-enrollment">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="everyone">Everyone in its audience</SelectItem>
        <SelectItem value="optin">Members who join it</SelectItem>
        <SelectItem value="invite">Invited members only</SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * The steps each kind can use (#4215), mirroring the server's kindStepViolation: a message
 * is a click step that is not an artist-match one, artist cards are the three match steps,
 * and a bounty is anything that is not series-only.
 */
export function stepsForKind(catalog: BountyCatalogEntry[], kind: BountyKind): BountyCatalogEntry[] {
  return catalog.filter((entry) => {
    const cards = suggestionAxisFor(entry.code) !== null;
    if (kind === 'suggestions') return cards;
    if (kind === 'message') return !cards && entry.completion === 'click';
    return !entry.isSeriesItemOnly;
  });
}

const POINTS_HINT: Record<BountyKind, string> = {
  task: 'Paid when the step is done.',
  message: 'Optional. Paid when they open it.',
  suggestions: 'Optional. Paid when they Connect or Follow from the card.',
};

function KindSelect({
  value,
  disabled,
  onChange,
}: {
  value: BountyKind;
  disabled?: boolean;
  onChange: (value: BountyKind) => void;
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={(next) => onChange(next as BountyKind)}>
      <SelectTrigger id="b-kind" data-testid="bounty-kind-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="task">{KIND_LABEL.task} — a step to do, for points</SelectItem>
        <SelectItem value="message">{KIND_LABEL.message} — a line and a button</SelectItem>
        <SelectItem value="suggestions">{KIND_LABEL.suggestions} — artists for one match</SelectItem>
      </SelectContent>
    </Select>
  );
}

type WordField = readonly ['label' | 'why' | 'evidence' | 'cta', string, string, string | null | undefined];

/** The copy fields, named for where each one shows for this kind. */
function wordFields(kind: BountyKind, step: BountyCatalogEntry | null): WordField[] {
  if (kind === 'task') {
    return [
      ['label', 'Headline', 'On the pin at the top of the page, and as the inbox notification.', step?.defaultLabel],
      [
        'why',
        'Why it matters',
        'Under the name on the bounty’s own page, in the email, and on /points.',
        step?.defaultWhy,
      ],
      [
        'evidence',
        'Supporting fact',
        'On the bounty’s own page, under “Why it matters”. One line that backs it up.',
        step?.defaultEvidence,
      ],
    ];
  }
  const cards = kind === 'suggestions';
  const fields: WordField[] = [
    [
      'label',
      'Title',
      cards ? 'Over the artist cards, and as the email subject.' : 'The inbox line, and the email subject.',
      step?.defaultLabel,
    ],
    [
      'evidence',
      cards ? 'Subtitle' : 'Body',
      cards ? 'Under the title.' : 'Under the title, in the inbox and the email.',
      step?.defaultEvidence,
    ],
    ['why', 'Email paragraph', 'In the email, after the body.', step?.defaultWhy],
  ];
  if (!cards) fields.push(['cta', 'Button', 'The button in the inbox and the email.', step?.defaultCta ?? 'Open it']);
  return fields;
}

export type EmailMode = 'off' | 'digest' | 'own';

export function emailModeOf(form: Pick<BountyForm, 'channelEmail' | 'channelDigest'>): EmailMode {
  if (form.channelEmail) return 'own';
  return form.channelDigest ? 'digest' : 'off';
}

const EMAIL_DETAIL: Record<EmailMode, string> = {
  off: 'No email. Members see it in the app only.',
  digest:
    'No extra email. It becomes a line in that night’s notification email, and can be its subject line. Needs Inbox on.',
  own: 'A separate email just for this, 08:00–21:00 their time. Across every bounty, a member gets at most one of these a day — extras wait for the next day.',
};

/**
 * WHERE ITS EMAIL GOES (Nick, 2026-10-13). A line in the nightly notification email is the
 * default — no extra email. Its own email is a deliberate choice, and on a recurring
 * bounty it starts as one announcement, never one per period, unless an admin says so.
 */
function EmailChoice({
  form,
  setField,
  period,
}: {
  form: BountyForm;
  setField: SetField;
  period: Exclude<BountyForm['cadence'], 'once' | 'each'> | null;
}) {
  const mode = emailModeOf(form);
  return (
    <div className="flex flex-wrap items-start gap-3 py-2.5" data-testid="bounty-channel-email">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">Email</p>
        <p className="text-xs text-gray-500" data-testid="bounty-email-detail">
          {EMAIL_DETAIL[mode]}
        </p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <Select
          value={mode}
          onValueChange={(next) => {
            const value = next as EmailMode;
            setField('channelEmail', value === 'own');
            setField('channelDigest', value === 'digest');
            if (value === 'digest') setField('channelNotify', true);
            // One announcement unless an admin chooses a rhythm.
            if (value === 'own' && period && !form.emailReminder) setField('emailReminder', 'first');
          }}
        >
          <SelectTrigger className="h-8 w-64 text-xs" data-testid="bounty-email-mode" aria-label="Email">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Off</SelectItem>
            <SelectItem value="digest">In the daily notification email</SelectItem>
            <SelectItem value="own">Its own email</SelectItem>
          </SelectContent>
        </Select>
        {mode === 'own' && period ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">How often</span>
            <ReminderSelect
              channel="email"
              value={form.emailReminder}
              cadence={period}
              onChange={(value) => setField('emailReminder', value)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function audienceScope(form: Pick<BountyForm, 'inSeries' | 'enrollment'>): string {
  if (form.inSeries) return 'Narrows which new members in the series get it.';
  if (form.enrollment === 'invite') return 'Narrows which invited members it pays.';
  if (form.enrollment === 'optin') return 'Narrows who can see and join it.';
  return 'Narrows who sees it and is paid.';
}

/**
 * Rules that hold a bounty back from members who are too new, not yet activated, too light
 * on reputation — or that aim it at complete or incomplete profiles. A member a rule rules
 * out neither sees the bounty nor is paid by it.
 */
function MemberRules({ form, setField }: { form: BountyForm; setField: SetField }) {
  return (
    <div
      className="min-w-0 max-w-full space-y-3 rounded-md border border-gray-200 p-3"
      data-testid="bounty-member-rules"
    >
      <label className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
        Hide from members who signed up less than
        <Input
          className="w-20"
          inputMode="numeric"
          value={form.minAccountAgeDays}
          placeholder="0"
          onChange={(e) => setField('minAccountAgeDays', digitsOnly(e.target.value))}
          data-testid="bounty-min-account-age"
        />
        days ago
      </label>
      <label className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
        Only members with at least
        <Input
          className="w-24"
          inputMode="numeric"
          value={form.minReputationPoints}
          placeholder="0"
          onChange={(e) => setField('minReputationPoints', digitsOnly(e.target.value))}
          data-testid="bounty-min-reputation"
        />
        reputation points
      </label>
      <label className="flex items-start gap-2 text-sm">
        <Switch
          checked={form.requireTier2Activated}
          onCheckedChange={(checked) => setField('requireTier2Activated', checked)}
          data-testid="bounty-require-activated"
        />
        <span className="min-w-0">Only members who have activated (uploaded on 2 days in their first 2 weeks)</span>
      </label>
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
        <Label htmlFor="b-profile-completeness">Profile</Label>
        <Select
          value={form.profileCompleteness}
          onValueChange={(value) => setField('profileCompleteness', value as BountyForm['profileCompleteness'])}
        >
          <SelectTrigger
            id="b-profile-completeness"
            className="w-full min-w-0 max-w-full sm:max-w-72"
            data-testid="bounty-profile-completeness"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any profile</SelectItem>
            <SelectItem value="complete">Only complete profiles (100%)</SelectItem>
            <SelectItem value="incomplete">Only incomplete profiles (under 100%)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <Switch
          checked={form.requireHumanMadeArt}
          onCheckedChange={(checked) => setField('requireHumanMadeArt', checked)}
          data-testid="bounty-require-hma"
        />
        <span className="min-w-0">Only Human Made Art members (active or trialing)</span>
      </label>
      <label className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
        Only members who completed at least
        <Input
          className="w-20"
          inputMode="numeric"
          value={form.minBountiesCompleted}
          placeholder="0"
          onChange={(e) => setField('minBountiesCompleted', digitsOnly(e.target.value))}
          data-testid="bounty-min-bounties"
        />
        bounties
      </label>
      <ChaserHint />
      <p className="text-xs text-gray-500">Leave a box blank for no minimum.</p>
    </div>
  );
}

/** Where the bounty chasers start, so the minimum can aim at the top 10% without guessing. */
function ChaserHint() {
  const { data } = useQuery<{ adminBountyChasers: Record<'members' | 'p50' | 'p90' | 'p95' | 'p99' | 'max', number> }>(
    ADMIN_BOUNTY_CHASERS
  );
  const chasers = data?.adminBountyChasers;
  if (!chasers || chasers.members === 0) return null;
  return (
    <p className="text-xs text-gray-500" data-testid="bounty-chaser-hint">
      Of {chasers.members.toLocaleString()} {chasers.members === 1 ? 'member who has' : 'members who have'} done one:
      half have done {chasers.p50}, the top 10% {chasers.p90}+, the top 5% {chasers.p95}+, the top 1% {chasers.p99}+
      (most: {chasers.max}).
    </p>
  );
}

export function StepBountyForm({
  form,
  setField,
  catalog,
  step,
  verdict,
  live,
  isNew,
  slugFrom,
}: {
  form: BountyForm;
  setField: SetField;
  catalog: BountyCatalogEntry[];
  step: BountyCatalogEntry | null;
  verdict: (entry: BountyCatalogEntry) => string | null;
  live: boolean;
  isNew: boolean;
  slugFrom: (name: string) => string;
}) {
  const recurring = form.cadence === 'daily' || form.cadence === 'weekly' || form.cadence === 'monthly';
  const period = recurring ? (form.cadence as Exclude<BountyForm['cadence'], 'once' | 'each'>) : null;
  const item = form.kind !== 'task';
  const steps = stepsForKind(catalog, form.kind);
  const targetError = bountyTargetError(form.target);

  return (
    <div className="min-w-0 space-y-6">
      <FormSection
        title="What it is"
        hint="A bounty asks for something and pays for it. A message or artist cards just show up on their day."
        testId="bounty-section-kind"
      >
        <div className="w-80 space-y-1">
          <Label htmlFor="b-kind">Kind</Label>
          <KindSelect
            value={form.kind}
            disabled={live || !isNew}
            onChange={(value) => {
              setField('kind', value);
              setField('stepCode', '');
              setField('target', '');
              if (value !== 'task') {
                // A message or card item belongs to the series, once, and pays nothing by default.
                setField('inSeries', true);
                setField('cadence', 'once');
                setField('pointsReward', '0');
                setField('channelPin', false);
                setField('channelEmail', false);
                setField('channelDigest', true);
              }
            }}
          />
        </div>
      </FormSection>

      <FormSection
        title={item ? `The ${KIND_LABEL[form.kind].toLowerCase()}` : 'The bounty'}
        testId="bounty-section-what"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_80px_100px]">
          <div className="space-y-1">
            <Label htmlFor="b-name">Name</Label>
            <Input
              id="b-name"
              value={form.name}
              onChange={(e) => {
                setField('name', e.target.value);
                if (isNew) setField('slug', slugFrom(e.target.value));
              }}
              placeholder="Rate 3 pieces"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="b-emoji">{item ? 'Icon' : 'Admin icon'}</Label>
            <Input
              id="b-emoji"
              value={form.emoji}
              onChange={(e) => setField('emoji', e.target.value)}
              placeholder="⭐"
            />
            <p className="text-[11px] text-gray-500">{item ? 'Members see this icon' : 'Members always see 🎯'}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="b-points">Points</Label>
            <Input id="b-points" value={form.pointsReward} onChange={(e) => setField('pointsReward', e.target.value)} />
            <p className="text-[11px] text-gray-500">{POINTS_HINT[form.kind]}</p>
          </div>
        </div>
        <div className="space-y-1">
          <Label>{item ? 'What it shows' : 'Step it pays for'}</Label>
          <Select
            value={form.stepCode}
            disabled={live}
            onValueChange={(value) => {
              setField('stepCode', value);
              // The gift is worked out per member on delivery; a pin would show its raw words.
              if (value === WELCOME_GIFT_STEP_CODE) setField('channelPin', false);
            }}
          >
            <SelectTrigger data-testid="bounty-step-select">
              <SelectValue placeholder={item ? 'Pick what it shows' : 'Pick the step it pays for'} />
            </SelectTrigger>
            <SelectContent>
              {steps.map((entry) => (
                <SelectItem key={entry.code} value={entry.code} disabled={Boolean(verdict(entry))}>
                  {entry.adminLabel}
                  {verdict(entry) ? ' — unavailable' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {step ? (
            <p className="text-xs text-gray-500">
              {step.adminMeasurement}
              {verdict(step) ? <span className="block text-amber-700">{verdict(step)}</span> : null}
            </p>
          ) : null}
        </div>
        {!item && isBoostableStep(form.stepCode) ? <MultiplierField form={form} setField={setField} /> : null}
        {step?.code === WELCOME_GIFT_STEP_CODE ? (
          <p className="text-xs text-gray-500" data-testid="bounty-gift-note">
            Its reward is worked out for each member when it is delivered. Write {'{points}'} (the points it pays),{' '}
            {'{reward}'} (the reward it unlocks) and {'{unlocked}'} (how many rewards she can now redeem) into the
            words. It is held back from anyone the reward is not redeemable for, and is skipped after its day. It cannot
            be pinned.
          </p>
        ) : null}
        {step && step.code !== WELCOME_GIFT_STEP_CODE && DYNAMIC_MESSAGE_STEP_CODES.includes(step.code) ? (
          <p className="text-xs text-gray-500" data-testid="bounty-dynamic-note">
            Its thread is picked when it is delivered. Write {'{title}'} and {'{replies}'} into the words to name it. On
            a day with nothing worth naming it waits, and is skipped after its day.
          </p>
        ) : null}
        {step?.code === TARGET_POSTS_STEP_CODE ? (
          <div className="space-y-1" data-testid="bounty-target-posts-field">
            <Label htmlFor="b-post-refs">Posts to comment on</Label>
            <Textarea
              id="b-post-refs"
              rows={4}
              value={form.postRefs}
              placeholder={'https://arthelper.com/community/general/post/how-much-do-you-charge\n…one per line'}
              onChange={(e) => setField('postRefs', e.target.value)}
            />
            <p className="text-[11px] text-gray-500">
              Paste each post's link, one per line (up to {MAX_TARGET_POSTS}). It counts how many different posts here a
              member comments on — Count to reach sets how many — and opens the next one they have not.
            </p>
          </div>
        ) : null}
        {step?.code === 'hotThread' ? (
          <div className="max-w-[240px] space-y-1">
            <Label htmlFor="b-min-replies">Replies before it counts as hot</Label>
            <Input
              id="b-min-replies"
              inputMode="numeric"
              value={form.minReplies}
              placeholder="25"
              onChange={(e) => setField('minReplies', e.target.value)}
            />
          </div>
        ) : null}
        {form.kind === 'suggestions' ? (
          <p className="text-xs text-gray-500" data-testid="bounty-cards-note">
            Up to four artists for this one match, never anyone a series card or the 14-day digest already showed them.
            Fewer than two and it is skipped.
          </p>
        ) : null}
        {!item && step?.bar ? (
          <div className="max-w-[200px] space-y-1">
            <Label htmlFor="b-target">Count to reach</Label>
            <Input
              id="b-target"
              type="number"
              min={1}
              step={1}
              value={form.target}
              disabled={live}
              placeholder={String(step.bar)}
              aria-invalid={targetError ? true : undefined}
              aria-describedby={targetError ? 'b-target-error' : undefined}
              onChange={(e) => setField('target', e.target.value)}
            />
            <p className="text-xs text-gray-500">The step&apos;s own is {step.bar}.</p>
            {targetError ? (
              <p id="b-target-error" role="alert" className="text-xs text-red-600">
                {targetError}
              </p>
            ) : null}
          </div>
        ) : null}
      </FormSection>

      <FormSection title="When it runs" testId="bounty-section-when">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full max-w-48 space-y-1">
            <Label htmlFor="b-cadence">How often</Label>
            <CadenceSelect
              value={form.cadence}
              disabled={live || form.inSeries}
              onChange={(value) => setField('cadence', value)}
            />
          </div>
          {item ? null : (
            <label className="flex min-h-9 min-w-0 flex-wrap items-center gap-2 text-sm">
              <Switch
                checked={form.inSeries}
                disabled={live}
                onCheckedChange={(checked) => {
                  setField('inSeries', checked);
                  if (checked) setField('cadence', 'once');
                }}
              />
              Part of the onboarding series
            </label>
          )}
          {form.inSeries ? (
            <label className="flex min-h-9 min-w-0 flex-wrap items-center gap-2 text-sm">
              Day
              <Input
                className="w-16"
                value={form.seriesDay}
                disabled={live && !item}
                onChange={(e) => setField('seriesDay', e.target.value)}
              />
              <span className="text-xs text-gray-500">from signup, 0–14</span>
            </label>
          ) : null}
        </div>
        {form.inSeries ? (
          <p className="text-xs text-gray-500" data-testid="bounty-cadence-why">
            {item
              ? 'It shows up once, on its day or the day after, to new members only. Its day can move while it is live.'
              : 'A series bounty happens once and pays new members only. Turn the series off to make it recur.'}
          </p>
        ) : null}
      </FormSection>

      <FormSection title="Who it's for" hint={audienceScope(form)} testId="bounty-audience">
        <div className="w-full max-w-64 space-y-1">
          <Label htmlFor="b-enrollment">Enrollment</Label>
          <EnrollmentSelect
            value={form.enrollment}
            disabled={live}
            onChange={(value) => setField('enrollment', value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="b-intents">Intents</Label>
            <Input
              id="b-intents"
              value={form.intents}
              placeholder="Anyone"
              onChange={(e) => setField('intents', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="b-stages">Career stages</Label>
            <Input
              id="b-stages"
              value={form.careerStages}
              placeholder="Anyone"
              onChange={(e) => setField('careerStages', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="b-countries">Countries</Label>
            <Input
              id="b-countries"
              value={form.countryCodes}
              placeholder="Anywhere"
              onChange={(e) => setField('countryCodes', e.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500">Comma-separated. Every filled field must match.</p>
        <MemberRules form={form} setField={setField} />
      </FormSection>

      <FormSection
        title="How members hear about it"
        hint="With all three off it is secret: it still pays, but nothing shows it."
        testId="bounty-section-delivery"
      >
        <div className="divide-y divide-gray-100 rounded-md border border-gray-200 px-3">
          <ChannelRow
            title="Inbox"
            detail="A row in the member's inbox, from 08:00 their time."
            checked={form.channelNotify}
            onCheckedChange={(checked) => {
              setField('channelNotify', checked);
              // The nightly email lists inbox rows; with no row there is nothing to list.
              if (!checked) setField('channelDigest', false);
            }}
            testId="bounty-channel-notify"
            reminder={
              period ? (
                <ReminderSelect
                  channel="notify"
                  value={form.notifyReminder}
                  cadence={period}
                  onChange={(value) => setField('notifyReminder', value)}
                />
              ) : null
            }
          />
          <ChannelRow
            title="Pin"
            disabled={form.kind === 'suggestions' || form.stepCode === WELCOME_GIFT_STEP_CODE}
            detail={
              period
                ? `The strip at the top of every page, until it is done each ${periodWord(period)}.`
                : 'The strip at the top of every page, until it is done.'
            }
            checked={form.channelPin}
            onCheckedChange={(checked) => setField('channelPin', checked)}
            testId="bounty-channel-pin"
          />
          <EmailChoice form={form} setField={setField} period={period} />
        </div>
        {item ? null : (
          <ChannelRow
            title="Show on /points"
            detail="Listed as a way to earn while it is live and open to everyone."
            checked={form.showOnPoints}
            onCheckedChange={(checked) => setField('showOnPoints', checked)}
            testId="bounty-show-on-points"
          />
        )}
        <ChannelRow
          title="List on /bounties"
          detail="Listed with its own page while it is live. Off keeps it running, unlisted."
          checked={form.showOnBounties}
          onCheckedChange={(checked) => setField('showOnBounties', checked)}
          testId="bounty-show-on-bounties"
        />
      </FormSection>

      <FormSection
        title="What members read"
        hint="The grey text is the step's own wording. Leave a field empty to keep it; type to replace it."
        testId="bounty-section-words"
      >
        {wordFields(form.kind, step).map(([key, title, where, placeholder]) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`b-${key}`}>{title}</Label>
            <p id={`b-${key}-where`} className="text-xs text-gray-500">
              Shows: {where}
            </p>
            <Input
              id={`b-${key}`}
              aria-describedby={`b-${key}-where`}
              value={form[key]}
              placeholder={placeholder ?? ''}
              onChange={(e) => setField(key, e.target.value)}
            />
          </div>
        ))}
      </FormSection>

      <FormSection title="Details" testId="bounty-section-details">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
          <div className="space-y-1">
            <Label htmlFor="b-slug">Page address</Label>
            <Input id="b-slug" value={form.slug} disabled={live} onChange={(e) => setField('slug', e.target.value)} />
            <p className="text-xs text-gray-500">/bounties/{form.slug || 'slug'}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="b-priority">Priority</Label>
            <Input id="b-priority" value={form.priority} onChange={(e) => setField('priority', e.target.value)} />
            <p className="text-xs text-gray-500">Lowest shows first.</p>
          </div>
        </div>
      </FormSection>
    </div>
  );
}

const MULTIPLIER_CHOICES = Array.from({ length: MULTIPLIER_MAX - MULTIPLIER_MIN + 1 }, (_, i) =>
  String(MULTIPLIER_MIN + i)
);

/**
 * The post that completes this bounty earns this many times on its upvotes and comments
 * (Nick, 2026-11-03). Its own Points are set above, never multiplied.
 */
function MultiplierField({ form, setField }: { form: BountyForm; setField: SetField }) {
  return (
    <div className="max-w-[320px] space-y-1" data-testid="bounty-multiplier">
      <Label>Multiplier on the post</Label>
      <Select
        value={form.multiplier || 'none'}
        onValueChange={(value) => setField('multiplier', value === 'none' ? '' : value)}
      >
        <SelectTrigger data-testid="bounty-multiplier-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None</SelectItem>
          {MULTIPLIER_CHOICES.map((choice) => (
            <SelectItem key={choice} value={choice}>
              {choice}×
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-gray-500">
        The post that completes it earns this many times the points on its upvotes and comments for {BOOST_DAYS} days.
        The daily limit on those payouts still applies.
      </p>
    </div>
  );
}
